import { useEffect, useMemo, useState } from "react";
import GlobalSVG from "../../utils/GlobalSVG";
import API from "../../utils/API";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

function AwaitingPurchases() {
    const [data, setData] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortConfig, setSortConfig] = useState({ key: "id", direction: "desc" });
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [mechanics, setMechanics] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedMechanicId, setSelectedMechanicId] = useState("");
    const [assignedMechanics, setAssignedMechanics] = useState([]);
    const [loadingAssigned, setLoadingAssigned] = useState(false);

    const navigate = useNavigate();

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return "-";
        return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    };

    const toggleSort = (key) => {
        setSortConfig((prev) => prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" });
    };

    const compareValues = (a, b, key, direction) => {
        if (key === "id") { const r = (a.id || 0) - (b.id || 0); return direction === "asc" ? r : -r; }
        if (key === "data") { const r = (new Date(a.data).getTime() || 0) - (new Date(b.data).getTime() || 0); return direction === "asc" ? r : -r; }
        const r = (a[key] || "").toString().toLowerCase().localeCompare((b[key] || "").toString().toLowerCase(), "pt-BR");
        return direction === "asc" ? r : -r;
    };

    const renderSortIcon = (key) => {
        const isActive = sortConfig.key === key;
        return (
            <span className="inline-flex flex-col ml-1">
                <span className={isActive && sortConfig.direction === "asc" ? "text-[#389E0D]" : "text-gray-300"}>{GlobalSVG.caretUp()}</span>
                <span className={`${isActive && sortConfig.direction === "desc" ? "text-[#389E0D]" : "text-gray-300"} -mt-1`}>{GlobalSVG.caretDown()}</span>
            </span>
        );
    };

    useEffect(() => {
        const u = JSON.parse(localStorage.getItem("yc_usr_dt_ls"));
        if (!u) return;
        setUser(u);

        Swal.fire({ title: "Carregando...", showConfirmButton: false, allowOutsideClick: false, willOpen: () => Swal.showLoading() });

        API.getServices(API.cancelToken())
            .then((res) => {
                // Services that need a mechanic assignment by the BOSS:
                // - "Aguardando Compras" / "AGUARDANDO PEÇA": legacy entry points
                // - "EM REPARO": parts already released by compras, ready for a mechanic
                const ASSIGNABLE = new Set([
                    "Aguardando Compras",
                    "AGUARDANDO PEÇA",
                    "EM REPARO",
                ]);
                const rows = (res.data || [])
                    .filter((item) => ASSIGNABLE.has(item.status || ""))
                    .map((item) => ({
                        id: Number(item.service_id),
                        license_plate: item.license_plate || "",
                        data: item.created_at,
                        client: item.name || "",
                        descricao: item.problem_description || "",
                        status: item.status || "",
                        assignedTo: item.assigned_to || null,
                    }));
                setData(rows);
            })
            .catch((err) => {
                console.error(err);
                Swal.fire("Erro", "Falha ao carregar serviços.", "error");
            })
            .finally(() => { setLoading(false); Swal.close(); });
    }, [navigate]);

    const viewRows = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const filtered = q
            ? data.filter((item) => [item.id, item.license_plate, item.client, item.descricao, formatDate(item.data)].map(String).some((v) => v.toLowerCase().includes(q)))
            : data;
        const copy = [...filtered];
        copy.sort((a, b) => compareValues(a, b, sortConfig.key, sortConfig.direction));
        return copy;
    }, [data, searchQuery, sortConfig]);

    // ---- Modal handlers ----
    const openAssignModal = async (row) => {
        setSelectedService(row);
        setSelectedMechanicId("");
        setAssignModalOpen(true);
        setLoadingAssigned(true);

        try {
            const [mecResp, assignedResp] = await Promise.all([
                API.getMecUsers(API.cancelToken()),
                API.getServiceMechanics(API.cancelToken(), row.id),
            ]);
            setMechanics(Array.isArray(mecResp.data) ? mecResp.data : []);
            setAssignedMechanics(Array.isArray(assignedResp.data) ? assignedResp.data : []);
        } catch (err) {
            console.error(err);
            setMechanics([]);
            setAssignedMechanics([]);
        } finally {
            setLoadingAssigned(false);
        }
    };

    const closeAssignModal = () => {
        setAssignModalOpen(false);
        setSelectedService(null);
        setSelectedMechanicId("");
        setAssignedMechanics([]);
    };

    const handleAssign = async () => {
        if (!selectedService?.id || !selectedMechanicId) return;

        // Check if already assigned
        if (assignedMechanics.some(m => String(m.user_id) === String(selectedMechanicId))) {
            Swal.fire("Aviso", "Este mecânico já está atribuído a esta OS.", "warning");
            return;
        }

        try {
            Swal.fire({ title: "Atribuindo mecânico...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            await API.assignWorker(API.cancelToken(), selectedService.id, selectedMechanicId);

            // Refresh assigned list
            const resp = await API.getServiceMechanics(API.cancelToken(), selectedService.id);
            setAssignedMechanics(Array.isArray(resp.data) ? resp.data : []);
            setSelectedMechanicId("");

            Swal.fire({ icon: "success", title: "Mecânico atribuído", timer: 1200, showConfirmButton: false });
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Falha ao atribuir o mecânico.", "error");
        }
    };

    const handleRemoveMechanic = async (userId, name) => {
        if (!selectedService?.id) return;
        const result = await Swal.fire({
            title: "Remover mecânico?",
            text: `Deseja remover ${name} desta OS?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Remover",
            cancelButtonText: "Cancelar",
        });
        if (!result.isConfirmed) return;

        try {
            await API.removeServiceMechanic(API.cancelToken(), selectedService.id, userId);
            setAssignedMechanics(prev => prev.filter(m => m.user_id !== userId));
            Swal.fire({ icon: "success", title: "Mecânico removido", timer: 1200, showConfirmButton: false });
        } catch (err) {
            console.error(err);
            Swal.fire("Erro", "Falha ao remover mecânico.", "error");
        }
    };

    // Filter out already-assigned mechanics from the dropdown
    const availableMechanics = mechanics.filter(
        m => !assignedMechanics.some(a => a.user_id === m.user_id)
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#389E0D]"></div>
            </div>
        );
    }

    return (
        <div className="flex w-full h-full pt-8 px-20 pb-24 items-center gap-8 self-stretch max-md:px-4">
            <div className="flex w-full h-auto py-6 px-11 flex-col items-center gap-6 self-stretch bg-white rounded-lg shadow max-md:px-4">
                {/* Header */}
                <div className="flex w-full justify-between items-center flex-wrap gap-4">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Atribuir Mecânico</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            {data.length} serviço{data.length !== 1 ? "s" : ""} aguardando atribuição
                        </p>
                    </div>
                    <div className="relative">
                        <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Pesquisar..."
                            className="border border-gray-300 rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent w-64"
                        />
                    </div>
                </div>

                {/* Empty state */}
                {data.length === 0 && !loading && (
                    <div className="flex flex-col items-center justify-center py-16 w-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-200 mb-4" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-400 text-lg font-medium">Tudo em dia!</p>
                        <p className="text-gray-300 text-sm mt-1">Nenhum serviço aguardando atribuição de mecânico.</p>
                    </div>
                )}

                {/* Table */}
                {data.length > 0 && (
                    <div className="w-full overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th onClick={() => toggleSort("id")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                        <div className="flex items-center gap-1">OS {renderSortIcon("id")}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Placa</th>
                                    <th onClick={() => toggleSort("data")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                        <div className="flex items-center gap-1">Data {renderSortIcon("data")}</div>
                                    </th>
                                    <th onClick={() => toggleSort("client")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer">
                                        <div className="flex items-center gap-1">Cliente {renderSortIcon("client")}</div>
                                    </th>
                                    <th onClick={() => toggleSort("descricao")} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer min-w-[200px]">
                                        <div className="flex items-center gap-1">Problema {renderSortIcon("descricao")}</div>
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {viewRows.length > 0 ? (
                                    viewRows.map((row) => (
                                        <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3">
                                                <span className="text-sm font-semibold text-gray-900">#{row.id}</span>
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.license_plate ? (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-xs font-mono font-semibold text-gray-700 tracking-wider">
                                                        {row.license_plate}
                                                    </span>
                                                ) : (
                                                    <span className="text-sm text-gray-300">-</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">{formatDate(row.data)}</td>
                                            <td className="px-4 py-3 text-sm text-gray-700 font-medium">{row.client || "-"}</td>
                                            <td className="px-4 py-3 min-w-[200px]">
                                                <p className="text-sm text-gray-600 line-clamp-2">{row.descricao || "-"}</p>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 whitespace-nowrap">
                                                    {row.status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openAssignModal(row)}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[#389E0D] hover:bg-green-700 rounded-lg transition-colors"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                                                        </svg>
                                                        Atribuir
                                                    </button>
                                                    <Link
                                                        to={`/create/service?id=${row.id}&step=5`}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[#389E0D] hover:bg-green-50 rounded-lg transition-colors"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                                        </svg>
                                                        Ver OS
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-8 text-gray-400 text-sm">
                                            Nenhum resultado para a pesquisa.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Assign Modal */}
            {assignModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeAssignModal} />
                    <div className="relative z-10 w-[95%] max-w-lg rounded-xl bg-white shadow-2xl">
                        <div className="px-6 py-5 border-b border-gray-100">
                            <h3 className="text-lg font-semibold text-gray-900">Atribuir Mecânicos</h3>
                            <p className="text-sm text-gray-500 mt-1">
                                OS <span className="font-semibold text-gray-700">#{selectedService?.id}</span>
                                {selectedService?.client && <> — {selectedService.client}</>}
                            </p>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Currently assigned mechanics */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Mecânicos atribuídos
                                    {assignedMechanics.length > 0 && (
                                        <span className="ml-2 text-xs font-normal text-gray-400">({assignedMechanics.length})</span>
                                    )}
                                </label>
                                {loadingAssigned ? (
                                    <div className="flex justify-center py-4">
                                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#389E0D]"></div>
                                    </div>
                                ) : assignedMechanics.length === 0 ? (
                                    <div className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3 text-center">
                                        Nenhum mecânico atribuído ainda.
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {assignedMechanics.map((m) => {
                                            const name = `${m.first_name || ""} ${m.last_name || ""}`.trim();
                                            return (
                                                <div key={m.user_id} className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-4 py-2.5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-8 w-8 rounded-full bg-[#389E0D] bg-opacity-20 flex items-center justify-center text-xs font-bold text-[#389E0D]">
                                                            {(m.first_name?.[0] || "")}{(m.last_name?.[0] || "")}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900">{name}</p>
                                                            {m.assigned_at && (
                                                                <p className="text-xs text-gray-400">
                                                                    Atribuído em {new Date(m.assigned_at).toLocaleDateString('pt-BR')}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={() => handleRemoveMechanic(m.user_id, name)}
                                                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remover mecânico"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Add new mechanic */}
                            {availableMechanics.length > 0 && (
                                <div className="border-t border-gray-100 pt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Adicionar mecânico</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <select
                                                className="appearance-none w-full pr-10 pl-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent transition-all"
                                                value={selectedMechanicId}
                                                onChange={(e) => setSelectedMechanicId(e.target.value)}
                                            >
                                                <option value="" disabled>Escolha um mecânico...</option>
                                                {availableMechanics.map((m) => (
                                                    <option key={m.user_id} value={m.user_id}>
                                                        {`${m.first_name || ""} ${m.last_name || ""}`.trim()}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                                <svg className="h-4 w-4 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={!selectedMechanicId}
                                            onClick={handleAssign}
                                            className={`px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors shrink-0 ${
                                                !selectedMechanicId
                                                    ? "bg-gray-300 cursor-not-allowed"
                                                    : "bg-[#389E0D] hover:bg-green-700"
                                            }`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {availableMechanics.length === 0 && mechanics.length > 0 && (
                                <p className="text-sm text-gray-400 text-center pt-2 border-t border-gray-100">
                                    Todos os mecânicos já foram atribuídos a esta OS.
                                </p>
                            )}

                            <div className="flex justify-end pt-2">
                                <button
                                    type="button"
                                    onClick={closeAssignModal}
                                    className="px-4 py-2 rounded-lg border-2 border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AwaitingPurchases;
